/** Recently-visited record list (localStorage) — the ⌘K palette's "Recent"
 *  group. `PageHeader` records detail-page visits automatically: the first
 *  numeric path segment marks a record URL, and everything up to and
 *  including it is the record's base path (tab suffixes drop out, so one
 *  record = one entry no matter which tab was open). Best-effort storage —
 *  quota/denial failures are swallowed. */

export interface RecentRecord {
  /** Record base path, e.g. `/tours/123`. */
  to: string;
  /** The page title at visit time (updated on revisit). */
  label: string;
  /** Area hint derived from the path, e.g. `tours` or `rbac / access-groups`. */
  area: string;
  ts: number;
}

const KEY = 'console.recents';
const MAX = 15;

export function readRecents(): RecentRecord[] {
  try {
    const raw = localStorage.getItem(KEY);
    const arr: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) return [];
    return arr.filter(
      (r): r is RecentRecord =>
        typeof r === 'object' &&
        r !== null &&
        typeof (r as RecentRecord).to === 'string' &&
        typeof (r as RecentRecord).label === 'string',
    );
  } catch {
    return [];
  }
}

/** The record base of a pathname: segments up to and including the FIRST
 *  numeric one (`/tours/123/departures` → `/tours/123`); null for paths with
 *  no numeric segment (list pages — those are nav targets, not records). */
export function recordBase(pathname: string): { to: string; area: string } | null {
  const segments = pathname.split('/'); // ['', 'tours', '123', ...]
  const idx = segments.findIndex((s, i) => i > 0 && /^\d+$/.test(s));
  if (idx < 2) return null;
  return {
    to: segments.slice(0, idx + 1).join('/'),
    area: segments.slice(1, idx).join(' / '),
  };
}

export function recordRecentVisit(pathname: string, label: string): void {
  const base = recordBase(pathname);
  if (!base || !label) return;
  const rest = readRecents().filter((r) => r.to !== base.to);
  const next: RecentRecord[] = [{ ...base, label, ts: Date.now() }, ...rest].slice(0, MAX);
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* best-effort */
  }
}
